import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { useGetBalanceHistoriesQuery } from "../../services/finance.service";
import { BalanceHistory } from "../../types/finance";
import { Company } from "../../types/company";
import dayjs from "dayjs";
import { formatMoney } from "../../utils/helper";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";


const CompanyFundsPanel = ({ data: dataCompany }: { data?: Company; }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const { data, isLoading, isFetching } = useGetBalanceHistoriesQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    modelType: "company",
    model: dataCompany?.id,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        minWidth: isMobile ? 140 : 200,
        flex: isMobile ? 0 : undefined,
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: true,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${dayjs(params.row?.created_at).format("MMM DD, YYYY hh:mm A")}`,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "notes",
        headerName: "Notes",
        minWidth: isMobile ? 200 : 400,
        flex: isMobile ? 0 : 1,
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: true,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "debit",
        headerName: "Debit",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 120,
        type: "number",
        sortable: false,
        filterable: false,
        align: "right",
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.debit)}`,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "credit",
        headerName: "Credit",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 120,
        type: "number",
        sortable: false,
        filterable: false,
        align: "right",
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.credit)}`,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "balance",
        headerName: "Balance",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 130,
        type: "number",
        sortable: false,
        filterable: false,
        align: "right",
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.balance)}`,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
    ],
    [isMobile, isTablet]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <CardBase
      title="Finances"
      useGlassCard={false}
      headerActions={
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <StyledTextField
            size="small"
            label="Enter to search.."
            value={query || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            sx={{
              minWidth: { xs: "100%", sm: 250 },
              width: { xs: "100%", sm: "auto" },
            }}
          />
          {!!query && (
            <Button
              variant="text"
              onClick={() => setQuery("")}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "auto" },
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                },
              }}
            >
              Reset
            </Button>
          )}
        </Stack>
      }
      sx={{
        height: { xs: "70vh", sm: "65vh" },
        p: 0,
      }}
    >
      <GlassDataGrid
        rows={rows || []}
        columns={columns}
        paginationMode="server"
        rowCount={rowCount}
        page={page}
        onPageChange={(newPage: number) => setPage(newPage)}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageSizeChange={(newSize: number) => setPageSize(newSize)}
        sortModel={sortModel}
        sortingMode="server"
        onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
        loading={isLoading || isFetching}
        components={{
          LoadingOverlay: OrangeLinearProgress,
          NoResultsOverlay: NoResultsOverlay,
        }}
        disableSelectionOnClick
        disableColumnFilter
        height={isMobile ? "calc(70vh - 100px)" : "calc(65vh - 80px)"}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "transparent",
            borderTop: "none",
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#F4A259",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
            },
            "& .MuiDataGrid-columnHeader": {
              color: "#F4A259",
              "& .MuiDataGrid-iconButtonContainer": {
                color: "#F4A259",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "#F4A259",
              },
            },
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(244, 162, 89, 0.05)",
          },
          "& .deleted": {
            fontStyle: "italic",
            color: "#aaa",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
          "& .MuiDataGrid-main": {
            overflowX: "auto",
          },
        }}
        getRowClassName={getRowClassName}
      />
    </CardBase>
  );
};
export default CompanyFundsPanel;
